import type { SupportChatMessage } from "@recruiter/shared/types";
import { useEffect, useRef } from "react";

import {
  optionUnitId,
  translated,
  unitId,
  type TranslationOverlay,
} from "../lib/conversation-translation";
import { widgetSummary } from "../lib/widget-summary";
import "./conversation-thread.css";

function messageTime(message: SupportChatMessage): string {
  const raw = message.createdAt ?? message.time;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** The operator who sent this reply, if a human did.
 *
 *  A takeover reply is deliberately seamless to the CLIENT (it renders as an
 *  ordinary assistant message), so the admin thread is the only place the
 *  difference is visible. `metadata` is an untyped bag on the wire shape, hence
 *  the runtime check. */
function operatorOf(message: SupportChatMessage): string | null {
  const sender = message.metadata?.["operator"];
  return typeof sender === "string" && sender ? sender : null;
}

/** A widget message, rendered as what it actually is: Mira's question, the
 *  choices she offered, and the client's answer once they gave one.
 *
 *  A widget's own `text` is empty (the question lives in `metadata.widget`), so
 *  this row used to print a stand-in — "Interactive prompt" — exactly where the
 *  conversation turned. The options matter as much as the question: without them
 *  a one-word answer three lines later reads as a non-sequitur.
 *
 *  Every string here goes through `translated`, because these rows are the ones a
 *  translation most has to reach: a question in Hebrew whose options are also in
 *  Hebrew is precisely what an operator opened the translation for. */
function WidgetBubble({
  messageId,
  summary,
  translations,
}: {
  messageId: string;
  summary: NonNullable<ReturnType<typeof widgetSummary>>;
  translations: TranslationOverlay | null;
}) {
  return (
    <div className="ct-message__widget">
      <p className="ct-widget__question">
        {translated(translations, unitId(messageId, "widget.question"), summary.question)}
      </p>
      {summary.options.length > 0 ? (
        <ul className="ct-widget__options">
          {summary.options.map((option, index) => (
            <li key={option}>{translated(translations, optionUnitId(messageId, index), option)}</li>
          ))}
        </ul>
      ) : null}
      {summary.answer ? (
        <p className="ct-widget__answer">
          <span className="ct-widget__answer-label">Answered:</span>{" "}
          {translated(translations, unitId(messageId, "widget.answer"), summary.answer)}
        </p>
      ) : summary.skipped ? (
        <p className="ct-widget__answer ct-widget__answer--skipped">Skipped by the client</p>
      ) : (
        <p className="ct-widget__answer ct-widget__answer--skipped">Not answered</p>
      )}
    </div>
  );
}

function MessageRow({
  message,
  clientName,
  translations,
}: {
  message: SupportChatMessage;
  clientName: string;
  translations: TranslationOverlay | null;
}) {
  const text = translated(translations, unitId(message.id, "text"), message.text);
  const title = translated(translations, unitId(message.id, "title"), message.title);
  const description = translated(
    translations,
    unitId(message.id, "description"),
    message.description,
  );

  if (message.type === "event") {
    return (
      <div className="ct-thread__event">
        <span>{text || title || "Conversation event"}</span>
        <time>{messageTime(message)}</time>
      </div>
    );
  }

  const client = message.type === "user";
  // Null for a non-widget message AND for a widget row too malformed to carry a
  // question — the latter falls through to the plain text render rather than
  // showing an empty card.
  const widget = widgetSummary(message);
  const operator = client ? null : operatorOf(message);
  return (
    <article className={`ct-message ${client ? "ct-message--client" : "ct-message--mira"}`}>
      <div className="ct-message__meta">
        <strong>{client ? clientName : "Mira"}</strong>
        {operator ? (
          <span className="ct-message__operator" title={`Sent by ${operator}`}>
            operator
          </span>
        ) : null}
        <time>{messageTime(message)}</time>
      </div>
      {widget ? (
        <WidgetBubble messageId={message.id} summary={widget} translations={translations} />
      ) : (
        <p>{text || title || description || "(empty message)"}</p>
      )}
    </article>
  );
}

export function ConversationThread({
  messages,
  clientName,
  translations = null,
}: {
  messages: SupportChatMessage[];
  clientName: string;
  /** An English reading of this transcript, overlaid field by field, or null for
   *  the original. A field the overlay does not carry renders as written, so a
   *  partial translation degrades to the real words rather than to a gap. */
  translations?: TranslationOverlay | null;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const element = scroller.current;
    if (element) element.scrollTop = element.scrollHeight;
  }, [messages.length]);

  return (
    <div className="ct-thread" ref={scroller} aria-label="Conversation transcript">
      {messages.length === 0 ? (
        <div className="ct-empty">No conversation yet.</div>
      ) : (
        messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            clientName={clientName}
            translations={translations}
          />
        ))
      )}
    </div>
  );
}
